import { useNavigate, useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { Home, CheckSquare, Bell, Briefcase, Search, X, Plus, ChevronRight, MoreHorizontal, BarChart2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export function MobileNav({ onSearchOpen, onNewProject }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { projects, notifications, activeProjectId, setActiveProject } = useAppStore()
  const [projectsOpen, setProjectsOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const unread = notifications.filter((n) => !n.read).length
  const activeProjects = projects.filter((p) => !p.completed)
  const isOnProject = location.pathname.startsWith('/project')

  const tabs = [
    { id: 'home',     label: 'Home',      icon: Home,            path: '/home' },
    { id: 'tasks',    label: 'My Tasks',  icon: CheckSquare,     path: '/my-tasks' },
    { id: 'inbox',    label: 'Inbox',     icon: Bell,            path: '/inbox', badge: unread || null },
    { id: 'projects', label: 'Projects',  icon: Briefcase,       action: () => setProjectsOpen(true) },
    { id: 'more',     label: 'More',      icon: MoreHorizontal,  action: () => setMoreOpen(true) },
  ]

  const morePages = ['/reporting']

  function isActive(tab) {
    if (tab.id === 'projects') return isOnProject
    if (tab.id === 'more') return morePages.includes(location.pathname)
    if (tab.path) return location.pathname === tab.path
    return false
  }

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-border"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-stretch">
          {tabs.map((tab) => {
            const active = isActive(tab)
            return (
              <button
                key={tab.id}
                onClick={() => tab.action ? tab.action() : navigate(tab.path)}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-[3px] pt-2.5 pb-2 min-h-[52px] transition-transform active:scale-[0.88]',
                  active ? 'text-accent' : 'text-text-faint'
                )}
              >
                <div className="relative">
                  <tab.icon size={21} strokeWidth={active ? 2.2 : 1.8} />
                  {tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] rounded-full bg-accent text-[8px] font-bold text-black flex items-center justify-center px-0.5 leading-none">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium leading-none">{tab.label}</span>
              </button>
            )
          })}
        </div>
      </nav>

      {/* More bottom sheet */}
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              key="more-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/60"
              onClick={() => setMoreOpen(false)}
            />
            <motion.div
              key="more-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-2xl"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>
              <div className="flex items-center justify-between px-4 pb-3 pt-1 border-b border-border">
                <span className="text-sm font-semibold text-text">More</span>
                <button onClick={() => setMoreOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-text-faint active:scale-[0.97] transition-transform">
                  <X size={16} />
                </button>
              </div>
              <div className="px-2 py-2">
                {[
                  { label: 'Search', icon: Search, action: () => { setMoreOpen(false); onSearchOpen?.() } },
                  { label: 'Reporting', icon: BarChart2, path: '/reporting' },
                ].map(({ label, icon: Icon, path, action }) => (
                  <button
                    key={label}
                    onClick={() => { if (action) { action() } else { navigate(path); setMoreOpen(false) } }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 h-14 rounded-xl transition-colors text-left active:bg-surface-raised',
                      path && location.pathname === path ? 'text-accent bg-accent/8' : 'text-text'
                    )}
                  >
                    <Icon size={20} strokeWidth={1.8} />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Projects bottom sheet */}
      <AnimatePresence>
        {projectsOpen && (
          <>
            <motion.div
              key="projects-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/60"
              onClick={() => setProjectsOpen(false)}
            />
            <motion.div
              key="projects-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-2xl flex flex-col"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)', maxHeight: '80vh' }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 pb-3 pt-1 border-b border-border flex-shrink-0">
                <span className="text-sm font-semibold text-text">Projects</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onNewProject?.(); setProjectsOpen(false) }}
                    className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-accent/15 text-accent text-xs font-semibold transition-colors active:scale-[0.97]"
                  >
                    <Plus size={12} />
                    New
                  </button>
                  <button
                    onClick={() => setProjectsOpen(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-text-faint active:scale-[0.97] transition-transform"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Project list */}
              <div className="overflow-y-auto flex-1">
                {activeProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Briefcase size={24} className="text-text-faint" />
                    <p className="text-sm text-text-muted">No projects yet</p>
                    <button
                      onClick={() => { onNewProject?.(); setProjectsOpen(false) }}
                      className="mt-1 px-4 h-9 rounded-lg bg-accent/15 text-accent text-sm font-semibold active:scale-[0.97]"
                    >
                      Create your first project
                    </button>
                  </div>
                ) : (
                  activeProjects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setActiveProject(p.id)
                        navigate(`/project/${p.id}`)
                        setProjectsOpen(false)
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 h-14 transition-colors text-left active:bg-surface-raised',
                        activeProjectId === p.id && isOnProject ? 'bg-surface-raised' : ''
                      )}
                    >
                      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: p.color }} />
                      <span className="flex-1 text-sm font-medium text-text truncate">{p.name}</span>
                      {activeProjectId === p.id && isOnProject && (
                        <span className="text-[10px] text-accent font-semibold mr-1">Active</span>
                      )}
                      <ChevronRight size={14} className="text-text-faint flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
